// /home/bigbrain/projects/smashparty/src/client/ui/components/input-field.ts
/**
 * Plan 23a, Task 2.2 — Input Field primitive
 *
 * Factory: createInputField(config: UIComponentConfig & InputFieldOpts)
 *   → UIComponentHandle & { value: string; setError(msg: string | null): void; focus(): void }
 *
 * Visual states:
 *   - default: white-ish bg, 2px border rgba(0,0,0,0.20), border-radius 12px, height 44px
 *   - focused : border color-brand-amber + glow-amber shadow (via JS class toggle + transition)
 *   - error   : border color-text-danger + error message span below
 *   - disabled: opacity 0.45, cursor not-allowed
 *
 * NO gsap — CSS transitions only. Focus state is toggled via JS class on the wrapper
 * because happy-dom does not honor CSS :focus pseudo-class, and to keep the transition
 * testable via class inspection.
 *
 * Design system references:
 *   - docs/design/tokens.json  — color, typography, spacing, border, animation values
 *   - docs/design/components.md §5 lines 202–227
 */

import type { UIComponentHandle, UIComponentConfig } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface InputFieldOpts {
  /** Placeholder text forwarded to the <input> element. */
  placeholder?: string;
  /** Initial value. Default: ''. */
  value?: string;
  /** Maximum character count forwarded to the <input> element. */
  maxLength?: number;
  /** Fired on every input event with the current value. */
  onChange?: (value: string) => void;
}

export interface InputFieldHandle extends UIComponentHandle {
  /** Current value of the underlying <input> element. */
  readonly value: string;
  /** Sets (or clears) the visible error message. Pass null to clear. */
  setError(msg: string | null): void;
  /** Programmatically focuses the underlying <input> element. */
  focus(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createInputField(config: UIComponentConfig & InputFieldOpts): InputFieldHandle {
  const { mount, placeholder = '', value: initialValue = '', maxLength, onChange } = config;

  // ── Create DOM ────────────────────────────────────────────────────────────
  // Structure:
  //   <div class="ui-input-field">            ← wrapper (handle.element)
  //     <input type="text" />                 ← the actual text input
  //     <span data-error style="…"></span>    ← error message (initially empty)
  //   </div>

  const wrapper = document.createElement('div');
  wrapper.className = 'ui-input-field';

  // Wrapper layout: position:relative for possible future absolute error positioning,
  // display:flex column so error span flows below the input.
  wrapper.style.cssText = [
    `display: flex`,
    `flex-direction: column`,
    `gap: var(--spacing-xs, 4px)`,
    `position: relative`,
    `box-sizing: border-box`,
  ].join('; ');

  // ── Input element ─────────────────────────────────────────────────────────

  const inputEl = document.createElement('input');
  inputEl.type = 'text';

  if (placeholder) {
    inputEl.placeholder = placeholder;
  }
  if (maxLength !== undefined) {
    inputEl.maxLength = maxLength;
  }
  inputEl.value = initialValue;

  // Input style — mirrors token values with CSS-var references + fallbacks.
  // Focused state is toggled by JS class (wrapper.classList.toggle('focused'))
  // because pseudo-classes aren't reliably reflected in happy-dom.
  inputEl.style.cssText = [
    `width: 100%`,
    `height: var(--layout-input-height, 44px)`,
    `box-sizing: border-box`,
    `background: var(--color-ui-input-bg, #F0F0F0)`,
    `color: var(--color-ui-input-text, #333333)`,
    `border: var(--border-input, 2px solid rgba(0,0,0,0.20))`,
    `border-radius: var(--border-radius-lg, 12px)`,
    `padding: var(--spacing-sm, 8px) var(--spacing-md, 16px)`,
    `font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)`,
    `font-size: var(--typography-size-body, 16px)`,
    `font-weight: var(--typography-weight-medium, 500)`,
    `outline: none`,
    `transition: border-color var(--animation-duration-fast, 150ms), box-shadow var(--animation-duration-fast, 150ms)`,
    `cursor: text`,
  ].join('; ');

  // ── Error span ────────────────────────────────────────────────────────────

  const errorSpan = document.createElement('span');
  errorSpan.setAttribute('data-error', '');
  errorSpan.style.cssText = [
    `font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)`,
    `font-size: var(--typography-size-caption, 14px)`,
    `color: var(--color-text-danger, #E04040)`,
    `min-height: 1em`,
    `display: block`,
  ].join('; ');
  errorSpan.textContent = '';

  wrapper.appendChild(inputEl);
  wrapper.appendChild(errorSpan);

  // ── Focused state helpers ─────────────────────────────────────────────────

  function applyFocused(): void {
    inputEl.style.borderColor = `var(--color-brand-amber, #E8A020)`;
    inputEl.style.boxShadow = `var(--shadow-glow-amber, 0 0 12px rgba(232,160,32,0.50))`;
    wrapper.classList.add('focused');
  }

  function clearFocused(): void {
    // Revert to default border from the border token
    inputEl.style.borderColor = ``;
    inputEl.style.boxShadow = ``;
    wrapper.classList.remove('focused');
    // Re-apply error border if in error state
    if (wrapper.classList.contains('error') || wrapper.hasAttribute('data-has-error')) {
      applyError();
    }
  }

  function applyError(): void {
    inputEl.style.borderColor = `var(--color-text-danger, #E04040)`;
    inputEl.style.boxShadow = ``;
  }

  // ── Event listeners ────────────────────────────────────────────────────────

  let disposed = false;

  function onInput(): void {
    if (onChange) {
      onChange(inputEl.value);
    }
  }

  function onFocus(): void {
    applyFocused();
  }

  function onBlur(): void {
    clearFocused();
  }

  inputEl.addEventListener('input', onInput);
  inputEl.addEventListener('focus', onFocus);
  inputEl.addEventListener('blur', onBlur);

  // ── Mount ──────────────────────────────────────────────────────────────────

  if (mount) {
    mount.appendChild(wrapper);
  }

  // ── Public handle methods ──────────────────────────────────────────────────

  function setError(msg: string | null): void {
    if (msg !== null) {
      errorSpan.textContent = msg;
      wrapper.classList.add('error');
      wrapper.setAttribute('data-has-error', '');
      // Apply error border (unless currently focused, where amber takes precedence)
      if (!wrapper.classList.contains('focused')) {
        applyError();
      }
    } else {
      errorSpan.textContent = '';
      wrapper.classList.remove('error');
      wrapper.removeAttribute('data-has-error');
      // Restore default border
      if (!wrapper.classList.contains('focused')) {
        inputEl.style.borderColor = '';
        inputEl.style.boxShadow = '';
      }
    }
  }

  function focus(): void {
    inputEl.focus();
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    inputEl.removeEventListener('input', onInput);
    inputEl.removeEventListener('focus', onFocus);
    inputEl.removeEventListener('blur', onBlur);

    if (wrapper.parentNode) {
      wrapper.parentNode.removeChild(wrapper);
    }
  }

  return {
    element: wrapper,
    dispose,
    get value(): string {
      return inputEl.value;
    },
    setError,
    focus,
  };
}
